import { Link, useNavigate } from "react-router-dom";
import { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo, SyntheticEvent } from "react";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import Header from "@/components/Header";
import JourneyBanner from "@/components/JourneyBanner";
import HomepageBanner from "@/components/HomepageBanner";
import { useBannerStage } from "@/hooks/useBannerStage";
import { bannerIndex, bannerSwipe } from "@/lib/bannerCarousel";
import { readJourneyBanner, JourneyBannerContent } from "@/lib/journeyBanner";
import Footer from "@/components/Footer";
import VideoPopup from "@/components/VideoPopup";
import BannerPopup from "@/components/BannerPopup";
import ButterflyAnimation from "@/components/ButterflyAnimation";
import { HomeSEO } from "@/components/SEO";
import { useBanners, Banner } from "@/hooks/useBanners";
import { useHomepageSections } from "@/hooks/useHomepageSections";
import { useContactDetails } from "@/hooks/useContactDetails";
import { useApplyCTAPosition } from "@/hooks/useSiteSettings";
import HomepageSectionComponent from "@/components/HomepageSection";
import { getOptimizedBannerUrl } from "@/lib/imageOptimization";
import heroBeachSunset from "@/assets/hero-beach-sunset.png";
import heroDesertBox from "@/assets/hero-desert-box.png";
import heroFlowerField from "@/assets/hero-flower-field.png";
import heroCorporateEvent from "@/assets/hero-corporate-event.png";
import heroSpecialOccasions from "@/assets/hero-special-occasions.png";

// Detect iOS for preloading optimizations
const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/i.test(ua) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
};

// Track which assets have been preloaded to avoid duplicate work
const preloadedAssets = new Set<string>();

// Preload images for smoother transitions - optimized for all browsers
const preloadImage = (src: string, priority: 'high' | 'low' = 'high') => {
  if (preloadedAssets.has(src)) return;
  preloadedAssets.add(src);
  
  // Use link preload for high priority only (reduces head bloat)
  if (priority === 'high') {
    const existingLink = document.querySelector(`link[href="${src}"]`);
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.crossOrigin = 'anonymous';
      (link as any).fetchpriority = 'high';
      document.head.appendChild(link);
    }
  }
  
  // Always create Image object for actual caching
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.decoding = priority === 'high' ? 'sync' : 'async';
  img.src = src;
};

// Lightweight video preload - optimized for all browsers
const preloadVideo = (src: string, priority: 'high' | 'low' = 'high') => {
  if (preloadedAssets.has(src)) return;
  preloadedAssets.add(src);
  const isIOS = isIOSDevice();
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

  // For high priority, use link preload (works well on Chrome/Edge/Firefox)
  if (priority === 'high' && !isIOS) {
    const existingLink = document.querySelector(`link[href="${src}"]`);
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'video';
      link.href = src;
      link.crossOrigin = 'anonymous';
      (link as any).fetchpriority = 'high';
      document.head.appendChild(link);
    }
  }

  // Create hidden video element for actual buffering
  const video = document.createElement('video');
  // Desktop: use 'auto' to fully buffer; Mobile/iOS: use 'metadata' to save bandwidth
  video.preload = isDesktop && !isIOS ? 'auto' : 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  
  // iOS doesn't like crossOrigin on some CDNs
  if (!isIOS) {
    video.crossOrigin = 'anonymous';
  }
  
  video.src = src;
  video.load();
  
  // For desktop high priority, try to buffer more aggressively
  if (isDesktop && priority === 'high' && !isIOS) {
    video.play().then(() => {
      setTimeout(() => {
        video.pause();
        video.currentTime = 0;
      }, 50);
    }).catch(() => {
      // Autoplay blocked, that's fine - load() already started buffering
    });
  }
};

// Detect if we're on a landscape screen to only preload the needed video
const isLandscapeScreen = () => {
  if (typeof window === 'undefined') return true;
  return window.innerWidth >= 1024; // lg breakpoint
};

// Preload a single slide's assets - only preload what's needed for current orientation
const preloadSlideAssets = (slide: {
  image: string;
  landscapeImage: string | null;
  background_type: string;
  video_url: string | null;
  landscape_video_url: string | null;
  // Optional originals (non-optimized) for Safari/iOS fallback
  originalImage?: string;
  originalLandscapeImage?: string | null;
}, priority: 'high' | 'low' = 'high') => {
  if (!slide) return;
  const isLandscape = isLandscapeScreen();
  // Always use optimized images — iOS supports WebP fine via render/image endpoint
  const portraitImg = slide.image;
  const landscapeImg = slide.landscapeImage || slide.image;
  if (slide.background_type === 'video') {
    // Only preload the video for current screen orientation to save bandwidth
    if (isLandscape) {
      const landscapeVideo = slide.landscape_video_url || slide.video_url;
      if (landscapeVideo) preloadVideo(landscapeVideo, priority);
      if (landscapeImg) preloadImage(landscapeImg, priority);
    } else {
      const portraitVideo = slide.video_url || slide.landscape_video_url;
      if (portraitVideo) preloadVideo(portraitVideo, priority);
      if (portraitImg) preloadImage(portraitImg, priority);
    }
  } else {
    // For image slides - only preload what's needed
    if (isLandscape) {
      if (landscapeImg) preloadImage(landscapeImg, priority);
    } else {
      if (portraitImg) preloadImage(portraitImg, priority);
    }
  }
};

// Fallback static slides when no banners in database
const fallbackSlides: (Omit<ReturnType<typeof bannerToSlide>, "journeyContent" | "show_butterflies" | "sourceBanner"> & { journeyContent?: JourneyBannerContent; show_butterflies?: boolean })[] = [{
  badge: "Crafted by AI",
  headline: ["The Perfect Gift", "For Someone You Love"],
  subheadline: "Personalized Perfume Box",
  description: "",
  image: heroBeachSunset,
  landscapeImage: null,
  originalImage: heroBeachSunset,
  originalLandscapeImage: null,
  banner_type: "standard",
  cta_text: "Create Their Scent",
  cta_link: "/quiz",
  text_bg_opacity: 0.2,
  background_type: 'image',
  video_url: null,
  landscape_video_url: null
}, {
  badge: "Crafted by AI",
  headline: ["Discover", "your essence"],
  subheadline: "\nA fragrance uniquely yours",
  description: "",
  image: heroDesertBox,
  landscapeImage: null,
  originalImage: heroDesertBox,
  originalLandscapeImage: null,
  banner_type: "standard",
  cta_text: "Create Your Scent",
  cta_link: "/quiz",
  text_bg_opacity: 0.2,
  background_type: 'image',
  video_url: null,
  landscape_video_url: null
}, {
  badge: "Crafted by AI",
  headline: ["A Colorful", "Floral Experience"],
  subheadline: "\nNature's finest blooms in every bottle",
  description: "",
  image: heroFlowerField,
  landscapeImage: null,
  originalImage: heroFlowerField,
  originalLandscapeImage: null,
  banner_type: "standard",
  cta_text: "Create Your Scent",
  cta_link: "/quiz",
  text_bg_opacity: 0.2,
  background_type: 'image',
  video_url: null,
  landscape_video_url: null
}, {
  badge: "Corporate Excellence",
  headline: ["Your Brand.", "Your Signature Scent."],
  subheadline: "Customized fragrances and bespoke packaging\ndesigned for corporate events and VIP clients.",
  description: "We create exclusive scents that reflect\nyour company's values and identity.",
  image: heroCorporateEvent,
  landscapeImage: null,
  originalImage: heroCorporateEvent,
  originalLandscapeImage: null,
  banner_type: "corporate",
  cta_text: "Contact Us",
  cta_link: "https://wa.me/971504996672",
  text_bg_opacity: 0.2,
  background_type: 'image',
  video_url: null,
  landscape_video_url: null
}, {
  badge: "Celebrate Life's Precious Moments",
  headline: ["Make It", "Unforgettable."],
  subheadline: "A bespoke fragrance for weddings, anniversaries & milestones.",
  description: "Let Anaya craft a signature scent for your special day.",
  image: heroSpecialOccasions,
  landscapeImage: null,
  originalImage: heroSpecialOccasions,
  originalLandscapeImage: null,
  banner_type: "special_occasion",
  cta_text: "Contact Us",
  cta_link: "https://wa.me/971504996672",
  text_bg_opacity: 0.2,
  background_type: 'image',
  video_url: null,
  landscape_video_url: null
}];

// Transform database banner to slide format with optimized images
// Language-aware banner to slide transformer
const bannerToSlide = (banner: Banner, lang: "en" | "ar" = "en") => {
  const l = <T,>(en: T, ar: T | null | undefined): T => {
    if (lang === "ar" && ar !== null && ar !== undefined && ar !== "") return ar;
    return en;
  };
  return {
    sourceBanner: banner,
    journeyContent: readJourneyBanner(banner.journey_content),
    badge: l(banner.badge, banner.badge_ar) || "",
    headline: [l(banner.headline_line1, banner.headline_line1_ar) || "", l(banner.headline_line2, banner.headline_line2_ar) || ""],
    subheadline: l(banner.subheadline, banner.subheadline_ar) || "",
    description: l(banner.description, banner.description_ar) || "",
    // Use optimized images for faster loading
    image: getOptimizedBannerUrl(banner.image_url, true) || banner.image_url,
    landscapeImage: getOptimizedBannerUrl(banner.landscape_image_url, false) || banner.landscape_image_url || null,
    // Keep original URLs for fallback
    originalImage: banner.image_url,
    originalLandscapeImage: banner.landscape_image_url || null,
    banner_type: banner.banner_type || "standard",
    cta_text: l(banner.cta_text, banner.cta_text_ar) || "Create Your Scent",
    cta_link: banner.cta_link || "/quiz",
    text_bg_opacity: banner.text_bg_opacity ?? 0.2,
    show_butterflies: banner.show_butterflies ?? false,
    background_type: banner.background_type || 'image',
    video_url: banner.video_url || null,
    landscape_video_url: banner.landscape_video_url || null
  };
};
const Index = () => {
  const {
    banners,
    loading
  } = useBanners();
  const {
    sections,
    getSection,
    getSectionContent
  } = useHomepageSections();
  const {
    formatWhatsAppLink
  } = useContactDetails();
  const { language } = useLanguage();

  // Apply iOS CTA position from database settings
  useApplyCTAPosition();
  const [currentSlide, setCurrentSlide] = useState(0);
  // The slide actually visible. currentSlide is the target; revealedSlide catches up
  // once the incoming media is decoded (bounded by REVEAL_TIMEOUT_MS), so the outgoing
  // slide stays visible instead of flashing blank.
  const [revealedSlide, setRevealedSlide] = useState(0);
  const [outgoingSlide, setOutgoingSlide] = useState<number | null>(null);
  const readySlidesRef = useRef<Set<number>>(new Set());
  const [readyTick, setReadyTick] = useState(0);
  const outgoingTimerRef = useRef<number | null>(null);
  const [showHeroText, setShowHeroText] = useState(false);
  const [videoBlocked, setVideoBlocked] = useState(false);

  // Touch/swipe state
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const [compactSlides, setCompactSlides] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 1023px)");
    const update = () => setCompactSlides(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const heroRef = useRef<HTMLElement>(null);
  useBannerStage(heroRef, loading);
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const autoSlideTimeoutRef = useRef<number | null>(null);
  const autoSlideTokenRef = useRef(0);
  // Attempt to play all hero videos - called on mount and on user interaction if blocked
  const attemptVideoPlay = useCallback(() => {
    const videos = videoRefs.current.filter(Boolean);
    Promise.allSettled(videos.map(video => {
      if (!video) return Promise.resolve();
      if (!video.paused) return Promise.resolve();
      return video.play();
    })).then(results => {
      const anyPlayed = results.some(r => r.status === "fulfilled");
      setVideoBlocked(!anyPlayed);
    });
  }, []);

  // Register video ref
  const registerVideoRef = useCallback((el: HTMLVideoElement | null) => {
    if (el && !videoRefs.current.includes(el)) {
      // iOS browsers (including Edge/Chrome on iOS) can be picky about inline playback.
      const ua = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/i.test(ua) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
      if (isIOS) {
        el.setAttribute("playsinline", "");
        el.setAttribute("webkit-playsinline", "");
      }
      videoRefs.current.push(el);
    }
  }, []);

  // Handle video play failure (iOS Safari/Chrome/Edge autoplay can be blocked or the play() promise can hang)
  const handleVideoCanPlay = useCallback((e: SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    const playPromise = video.play();
    const timeout = new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error("play_timeout")), 1400);
    });
    Promise.race([playPromise, timeout]).then(() => {
      // If we started playing, great.
      setVideoBlocked(false);
    }).catch(() => {
      // If blocked OR hanging, show the tap-to-play overlay.
      setVideoBlocked(true);
    });
  }, []);

  // Handle manual play button click
  const handlePlayClick = useCallback(() => {
    attemptVideoPlay();
  }, [attemptVideoPlay]);
  const isIOSPlatform = useMemo(() => {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/i.test(ua) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  }, []);
  const isSafariMobile = useMemo(() => {
    const ua = navigator.userAgent;
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
    return isIOSPlatform && isSafari;
  }, [isIOSPlatform]);
  // Use database banners if available, otherwise use fallback slides
  const heroSlides = useMemo(() => {
    if (!loading && banners.length > 0) {
      return banners.map(b => bannerToSlide(b, language));
    }
    return fallbackSlides;
  }, [banners, loading, language]);

  // LAZY PRELOADING: Only preload first slide on mount, then preload next slide before transition
  // This prevents bandwidth contention from loading all banners at once
  useEffect(() => {
    if (heroSlides.length === 0) return;

    // Only preload the first slide immediately
    const firstSlide = heroSlides[0];
    preloadSlideAssets(firstSlide, 'high');

    // Preload second slide after a short delay (for the upcoming auto-transition)
    if (heroSlides.length > 1) {
      const secondSlideTimeout = setTimeout(() => {
        preloadSlideAssets(heroSlides[1], 'low');
      }, 3000); // Preload 2nd slide after 3s, well before 10s auto-transition

      return () => clearTimeout(secondSlideTimeout);
    }
  }, [heroSlides]);

  // Preload next slide when current slide changes (for upcoming transition)
  useEffect(() => {
    if (heroSlides.length <= 1) return;

    // Preload the next slide in advance
    const nextIndex = (currentSlide + 1) % heroSlides.length;
    const nextSlide = heroSlides[nextIndex];

    // Small delay to not compete with current slide rendering
    const preloadTimeout = setTimeout(() => {
      preloadSlideAssets(nextSlide, 'low');
    }, 500);
    return () => clearTimeout(preloadTimeout);
  }, [currentSlide, heroSlides]);

  // Keep a stable CSS viewport unit ("--vh") using Visual Viewport when available.
  // iOS Safari sometimes reports a *smaller* visualViewport.height than the actually visible area;
  // using max(innerHeight, visualViewport.height) avoids under-sizing (which causes the next section to peek).
  useEffect(() => {
    let raf = 0;
    const setVH = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        const vvH = window.visualViewport?.height ?? window.innerHeight;

        // iOS: use visualViewport.height directly (matches *visible* area).
        // Others: guard against under-reporting by taking the max.
        const viewportHeight = isIOSPlatform ? vvH : Math.max(window.innerHeight, vvH);

        // px-precise height for Safari
        document.documentElement.style.setProperty('--vvh-px', `${viewportHeight}px`);
        const vh = viewportHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      });
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    window.visualViewport?.addEventListener('resize', setVH);
    // Fires when Safari UI expands/collapses without a traditional resize
    window.visualViewport?.addEventListener('scroll', setVH);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
      window.visualViewport?.removeEventListener('resize', setVH);
      window.visualViewport?.removeEventListener('scroll', setVH);
    };
  }, []);

  // Platform flag for CSS overrides (Ken Burns off + iOS-specific sizing tweaks)
  useLayoutEffect(() => {
    if (isIOSPlatform) {
      document.documentElement.dataset.platform = "ios";
    } else {
      delete document.documentElement.dataset.platform;
    }
  }, [isIOSPlatform]);

  // Measure the actual fixed header height (including safe-area padding) and expose it as a CSS var.
  // This makes the hero reliably fit on iOS devices across notch sizes / dynamic bars.
  useEffect(() => {
    const headerEl = document.querySelector('header');
    if (!headerEl) return;
    const setHeaderOffset = () => {
      const h = headerEl.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-offset', `${h}px`);
    };
    setHeaderOffset();
    const ro = new ResizeObserver(() => setHeaderOffset());
    ro.observe(headerEl);
    window.addEventListener('orientationchange', setHeaderOffset);
    window.visualViewport?.addEventListener('resize', setHeaderOffset);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', setHeaderOffset);
      window.visualViewport?.removeEventListener('resize', setHeaderOffset);
    };
  }, []);

  // Show hero text after 2 seconds delay
  useEffect(() => {
    const textTimer = setTimeout(() => {
      setShowHeroText(true);
    }, 2000);
    return () => clearTimeout(textTimer);
  }, []);
  const AUTO_SLIDE_MS = 10000;
  // Short opacity crossfade; keep in sync with the .banner-slide-layer transition in index.css.
  const FADE_MS = 340;
  // Bounded wait for incoming media before revealing anyway (slow network/autoplay failure).
  const REVEAL_TIMEOUT_MS = 1500;
  const markSlideReady = useCallback((index: number) => {
    if (!readySlidesRef.current.has(index)) {
      readySlidesRef.current.add(index);
      setReadyTick(tick => tick + 1);
    }
  }, []);
  const revealSlide = useCallback((target: number) => {
    setRevealedSlide(prev => {
      if (prev === target) return prev;
      setOutgoingSlide(prev);
      if (outgoingTimerRef.current !== null) window.clearTimeout(outgoingTimerRef.current);
      outgoingTimerRef.current = window.setTimeout(() => setOutgoingSlide(null), FADE_MS + 80);
      return target;
    });
  }, []);
  // Reveal the target slide once its media is ready; never leave the outgoing slide
  // up longer than the bounded fallback.
  useEffect(() => {
    if (currentSlide === revealedSlide) return;
    const slide = heroSlides[currentSlide];
    const readyImmediately = !slide || slide.banner_type === 'fragrance_journey';
    if (readyImmediately || readySlidesRef.current.has(currentSlide)) {
      revealSlide(currentSlide);
      return;
    }
    const timer = window.setTimeout(() => revealSlide(currentSlide), REVEAL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [currentSlide, revealedSlide, readyTick, heroSlides, revealSlide]);
  const performSlideChange = useCallback((delta: 1 | -1) => {
    setCurrentSlide(prev => bannerIndex(prev, delta, heroSlides.length));
  }, [heroSlides.length]);
  const stopAutoSlide = useCallback(() => {
    // Invalidate any in-flight tick closures
    autoSlideTokenRef.current += 1;
    if (autoSlideTimeoutRef.current !== null) {
      window.clearTimeout(autoSlideTimeoutRef.current);
      autoSlideTimeoutRef.current = null;
    }
  }, []);

  // Start or reset auto-slide (10 seconds).
  // Token-based invalidation guarantees that a "just-fired" timeout cannot advance
  // the slide right after a manual click.
  const scheduleAutoSlide = useCallback(() => {
    stopAutoSlide();
    autoSlideTokenRef.current += 1;
    const token = autoSlideTokenRef.current;
    const tick = () => {
      if (token !== autoSlideTokenRef.current) return;
      performSlideChange(1);
      autoSlideTimeoutRef.current = window.setTimeout(tick, AUTO_SLIDE_MS);
    };
    autoSlideTimeoutRef.current = window.setTimeout(tick, AUTO_SLIDE_MS);
  }, [performSlideChange, stopAutoSlide]);
  useEffect(() => {
    if (heroSlides.length <= 1) { stopAutoSlide(); return; }
    scheduleAutoSlide();
    return stopAutoSlide;
  }, [heroSlides, currentSlide, scheduleAutoSlide, stopAutoSlide]);
  const goToPrevSlide = useCallback(() => {
    performSlideChange(-1);
    scheduleAutoSlide();
  }, [performSlideChange, scheduleAutoSlide]);
  const goToNextSlide = useCallback(() => {
    performSlideChange(1);
    scheduleAutoSlide();
  }, [performSlideChange, scheduleAutoSlide]);

  const navigate = useNavigate();
  const didSwipeRef = useRef(false);

  // Touch handlers for swipe gestures
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = null;
    touchEndX.current = null;
    if ((e.target as HTMLElement).closest('.banner-navigation, .banner-actions, .journey-cta, .banner-play')) return;
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchEndX.current = null;
    didSwipeRef.current = false;
  }, [heroSlides, currentSlide]);
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch || touchStartX.current === null) return;
    touchEndX.current = touch.clientX;
    touchEndY.current = touch.clientY;
  }, []);
  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null || touchEndX.current === null) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    const delta = bannerSwipe({ x: touchStartX.current, y: touchStartY.current }, { x: touchEndX.current, y: touchEndY.current });
    if (delta) {
      didSwipeRef.current = true;
      if (delta > 0) {
        goToNextSlide();
      } else {
        goToPrevSlide();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  }, [goToNextSlide, goToPrevSlide]);

  // Click on banner = navigate to CTA link (skip if user swiped)
  const handleBannerClick = useCallback((e: React.MouseEvent) => {
    // Don't navigate if user clicked on a button, link, or interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]') || target.closest('video')) return;
    if (didSwipeRef.current) { didSwipeRef.current = false; return; }

    const slide = heroSlides[currentSlide] as any;
    if (!slide) return;

    const bannerType = slide.banner_type || 'standard';
    if (bannerType === 'fragrance_journey') return;
    {
      // Respect configured internal links for every type; retain contact fallback.
      const ctaLink = slide.cta_link || (bannerType === 'corporate' || bannerType === 'special_occasion' ? formatWhatsAppLink() : "/quiz");
      if (ctaLink.startsWith('http')) {
        window.open(ctaLink, '_blank', 'noopener,noreferrer');
      } else {
        if (ctaLink === "/quiz") sessionStorage.setItem("startFreshQuiz", "true");
        navigate(ctaLink);
      }
    }
  }, [heroSlides, currentSlide, navigate, formatWhatsAppLink]);

  // Scroll to top when landing page mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant"
    });
  }, []);

  // Reset video state when slide changes
  useEffect(() => {
    videoRefs.current = [];
    setVideoBlocked(false);
  }, [currentSlide]);

  // iOS/Edge iOS: if autoplay silently fails, show the tap-to-play overlay quickly.
  useEffect(() => {
    if (!isIOSPlatform) return;
    const pickVisible = () => videoRefs.current.find(v => {
      const r = v.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
    const t1 = window.setTimeout(() => {
      const v = pickVisible();
      if (!v) return;

      // If it hasn't progressed at all, try a nudge.
      if (v.currentTime <= 0.05) {
        v.play().catch(() => {});
      }
      const t2 = window.setTimeout(() => {
        const stillStuck = v.currentTime <= 0.05;
        if (stillStuck) setVideoBlocked(true);
      }, 800);
      return () => window.clearTimeout(t2);
    }, 1200);
    return () => window.clearTimeout(t1);
  }, [currentSlide, isIOSPlatform]);
  const currentSlideData = heroSlides[currentSlide];
  const isJourney = currentSlideData?.banner_type === 'fragrance_journey';
  const isCorporate = currentSlideData?.banner_type === 'corporate';
  const isSpecialOccasion = currentSlideData?.banner_type === 'special_occasion';
  const isStandard = currentSlideData?.banner_type === 'standard' || !isCorporate && !isSpecialOccasion;
  const textBgOpacity = currentSlideData?.text_bg_opacity ?? 0.2;
  const showTextBg = textBgOpacity > 0;
  const showButterflies = (currentSlideData as any)?.show_butterflies ?? false;
  const isVideoBackground = (currentSlideData as any)?.background_type === 'video' && ((currentSlideData as any)?.video_url || (currentSlideData as any)?.landscape_video_url);
  const renderSlide = (slide: typeof currentSlideData, active: boolean, index: number) => slide?.banner_type === "fragrance_journey"
    ? <JourneyBanner content={slide.journeyContent || readJourneyBanner(null)} language={language} />
    : slide ? <HomepageBanner language={language} active={active} warm={index === currentSlide} onReady={() => markSlideReady(index)} contactLink={formatWhatsAppLink()} banner={"sourceBanner" in slide ? slide.sourceBanner : {
      title: slide.headline[0], image_url: slide.originalImage || slide.image, landscape_image_url: slide.originalLandscapeImage || slide.landscapeImage,
      badge: slide.badge, headline_line1: slide.headline[0], headline_line2: slide.headline[1], subheadline: slide.subheadline, description: slide.description,
      cta_text: slide.cta_text, cta_link: slide.cta_link, banner_type: slide.banner_type, background_type: slide.background_type, video_url: slide.video_url, landscape_video_url: slide.landscape_video_url,
    }} /> : null;
  return <div className="min-h-screen bg-background">
      <HomeSEO />
      <Header />
      
      {/* Hero Section with Carousel - 9:16 aspect ratio */}
      <section ref={heroRef} className={`banner-carousel ${compactSlides ? "banner-carousel-compact" : ""} ${isJourney ? "journey-carousel" : ""} relative touch-pan-y bg-anaya-background`} onClick={handleBannerClick} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onTouchCancel={() => { touchStartX.current = null; touchEndX.current = null; }}>
        <div className={`banner-carousel-stage ${isJourney ? "journey-carousel-stage" : ""} relative w-full`}>
        {loading ? <div className="bg-anaya-background" aria-hidden="true" /> : (() => {
          // Mobile keeps every slide mounted (inert siblings reserve copy height).
          // Desktop mounts only the revealed slide, the incoming target and the
          // slide fading out, so the crossfade never stacks tall slides permanently.
          const mounted = compactSlides
            ? heroSlides.map((_, index) => index)
            : Array.from(new Set([revealedSlide, currentSlide, ...(outgoingSlide !== null ? [outgoingSlide] : [])]));
          return mounted.map(index => {
            const isRevealed = index === revealedSlide;
            const isFadingOut = index === outgoingSlide && !isRevealed;
            return <div key={index} data-banner-slide={index + 1} data-active={isRevealed} data-fading={isFadingOut || undefined} aria-hidden={!isRevealed} {...(!isRevealed ? { inert: "" } : {})} className="banner-slide-layer">{renderSlide(heroSlides[index], isRevealed, index)}</div>;
          });
        })()}

        </div>
        {!loading && <nav aria-label="Banner navigation" className="journey-navigation banner-navigation relative z-20 flex w-full items-center justify-center gap-3 bg-anaya-background" onClick={(e) => e.stopPropagation()}>
          <Button variant="outline" size="icon" className="h-11 w-11 shrink-0 rounded-full border-gold/40 bg-anaya-background text-anaya-ink hover:bg-gold/10" aria-label={language === "ar" ? "الشريحة السابقة" : "Previous slide"} onPointerDown={(e) => { if (e.pointerType === "mouse") e.preventDefault(); }} onClick={goToPrevSlide}><ChevronLeft className="h-5 w-5" /></Button>
          <div className="flex min-w-0 flex-wrap items-center justify-center">
            {heroSlides.map((_, index) => <Button key={index} variant="ghost" size="icon" className="h-11 w-8" aria-label={language === "ar" ? `انتقل إلى الشريحة ${index + 1}` : `Go to slide ${index + 1}`} aria-current={index === currentSlide ? "true" : undefined} onPointerDown={(e) => { if (e.pointerType === "mouse") e.preventDefault(); }} onClick={() => { stopAutoSlide(); setCurrentSlide(index); }}><span className={`h-2 w-2 rounded-full ${index === currentSlide ? "bg-gold" : "bg-gold/30"}`} /></Button>)}
          </div>
          <Button variant="outline" size="icon" className="h-11 w-11 shrink-0 rounded-full border-gold/40 bg-anaya-background text-anaya-ink hover:bg-gold/10" aria-label={language === "ar" ? "الشريحة التالية" : "Next slide"} onPointerDown={(e) => { if (e.pointerType === "mouse") e.preventDefault(); }} onClick={goToNextSlide}><ChevronRight className="h-5 w-5" /></Button>
        </nav>}
      </section>

      {/* Dynamic Homepage Sections - rendered in order from database */}
      {sections.filter(section => section.section_key !== 'hero') // Hero is handled by banners
    .map(section => <HomepageSectionComponent key={section.id} section={section} formatWhatsAppLink={formatWhatsAppLink} />)}

      <Footer />
      <VideoPopup />
      <BannerPopup />
    </div>;
};
export default Index;

